/**
 * Replaying an op log onto a (possibly drifted) edit — the mechanism behind
 * "Apply" on a proposal card (docs/agent.md §3).
 *
 * The agent built its op log against a draft cloned from the timeline as it was
 * when the run started. By the time a human clicks Apply, collaborators may
 * have changed things. Replay re-executes the same semantic ops against the
 * CURRENT edit, so the result is what the user asked for on the timeline they
 * are actually looking at — or an honest conflict.
 *
 * All-or-nothing: a failing op aborts the whole replay and nothing is applied.
 * A half-applied proposal is worse than a refused one, because the user has no
 * way to reason about what state they are now in.
 */
import { produce } from 'immer';
import { validateEdit } from '../../format/helpers';
import { normalizeAllTracks } from '../../format/invariants';
import type { Edit } from '../../format/types';
import type { Op } from '../core/types';
import { remapIdArgs } from './oplog';

/**
 * What replay needs to know about a tool. Injected rather than imported from
 * the catalog so replay stays a pure mechanism (and so its tests can drive it
 * with a two-line fake registry).
 */
type ApplyResult = { created: string[] } | { error: string };

export interface ReplayableTool {
  /** Mutates the draft in place and returns the ids it created, in order. */
  execute(draft: Edit, args: Record<string, unknown>): ApplyResult;
  idArgPaths?: string[];
}

export type ReplayRegistry = Record<string, ReplayableTool>;

export type ReplayResult =
  | { ok: true; edit: Edit; idMap: Map<string, string> }
  | { ok: false; conflict: string };

export function replayOps(base: Edit, ops: Op[], registry: ReplayRegistry): ReplayResult {
  // draft id (from the run) → live id (from this replay)
  const idMap = new Map<string, string>();
  let conflict: string | null = null;

  const next = produce(base, (draft) => {
    for (const op of ops) {
      const tool = registry[op.tool];
      if (!tool) {
        conflict = `unknown operation "${op.tool}"`;
        return;
      }

      // Rewrite ids the run created into the ids this replay created. Ids the
      // run did NOT create pass through untouched — they refer to things that
      // already existed on the base edit.
      const args = remapIdArgs(op.args, tool.idArgPaths, idMap);

      // Whether a missing id is fatal is per-tool knowledge, and `execute`
      // already has it: trim_clip refuses an unknown clip, while add_clip
      // tolerates an unknown target track by falling back to the first video
      // track. A pre-emptive existence check here was STRICTER than the tool
      // itself, so an op that succeeded at edit time could fail at apply time —
      // which is exactly what the multitrack eval hit.
      // An op read from the CRDT is UNTRUSTED input: anyone with the
      // collaboration link can write arbitrary JSON into the `agent` root, and
      // `isOp` only checks the envelope, not `args`. Executors cast their
      // resolved args freely, so a hostile or corrupted record throws — and
      // this runs inside a React render (ProposalCard's useMemo), where a throw
      // takes the whole chat panel down for every collaborator, permanently,
      // because the bad record stays in the document. Same discipline as
      // crdt/chat.ts: turn bad wire data into a refusal, never an exception.
      let result: ApplyResult;
      try {
        result = tool.execute(draft, args);
      } catch (err) {
        conflict =
          `"${op.tool}" could not be applied — its recorded arguments are malformed ` +
          `(${err instanceof Error ? err.message : String(err)})`;
        return;
      }
      if ('error' in result) {
        conflict = `"${op.tool}" no longer applies to the current timeline: ${result.error}`;
        return;
      }

      if (result.created.length !== op.produced.length) {
        // The op created a different number of things this time — the base
        // must have changed underneath in a way that changes the outcome.
        conflict =
          `"${op.tool}" produced ${result.created.length} item(s) on the current ` +
          `timeline but ${op.produced.length} when the assistant ran it`;
        return;
      }
      op.produced.forEach((draftId, i) => idMap.set(draftId, result.created[i]));

      // Normalize per op, matching the store: one action = one normalize. An op
      // log replayed with a single normalize at the end could diverge from what
      // the agent saw, because later ops read state the earlier normalize set.
      normalizeAllTracks(draft.tracks);
    }
  });

  if (conflict !== null) return { ok: false, conflict };

  // Not throwing is not the same as being correct: an executor handed malformed
  // args can complete "successfully" and leave a structurally broken clip —
  // verified with `{tool:'add_clip', args:{}}`, which produced a sourceless clip
  // with no in/out and reported ok. Applying that would reconcile the damage
  // into the shared document, so the result is checked before it escapes.
  //
  // Only NEW errors count: the live timeline may already be invalid for
  // unrelated reasons, and it is not this proposal's job to refuse over those.
  // "timeline is empty" is excluded outright — it is the correct outcome of
  // "delete everything", which is a legitimate thing to propose.
  const preexisting = new Set(validateEdit(base));
  const introduced = validateEdit(next).filter(
    (e) => e !== EMPTY_TIMELINE_ERROR && !preexisting.has(e),
  );
  if (introduced.length) {
    return {
      ok: false,
      conflict: `applying this would produce an invalid timeline: ${introduced.join('; ')}`,
    };
  }

  return { ok: true, edit: next, idMap };
}

/** validateEdit reports this for an empty project; deleting everything is legal. */
const EMPTY_TIMELINE_ERROR = 'timeline is empty';
